import { InMemoryCache, createHttpLink, split } from '@apollo/client/core'
import { onError } from '@apollo/client/link/error'
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename'
import { createUploadLink } from 'apollo-upload-client'
import { scrubSensitive } from '~/utils/scrubSensitive'

export default (app: any, _route: any, _redirect: any, _ctx: any) => {
  let tokenRefreshPromise: Promise<unknown> | null = null

  const errorLink = onError((err) => {
    const { graphQLErrors, networkError, operation, response } = err
    const suppressUserError = Boolean(operation.getContext()?.suppressUserError)

    if (networkError) {
      app.$sentry?.captureException(networkError)
    }
    if (graphQLErrors) {
      if (!suppressUserError) {
        app.$sentry?.withScope((scope) => {
          // Annotate whether failing operation was query/mutation/subscription
          scope.setTag('kind', operation.operationName)
          // Log query and scrubbed variables as extras (F-044)
          scope.setExtra('query', JSON.stringify(operation.query))
          scope.setExtra('variables', scrubSensitive(operation.variables))
          graphQLErrors.map(({ message }) =>
            app.$sentry?.captureException(message, err),
          )
        })

        window.$nuxt.$emit('userError', graphQLErrors?.[0])
      }
    }
  })

  function generateAcceptLanguageString() {
    // TODO remove redundant languages
    let languages = navigator.languages
    // @ts-ignore
    if (window.$nuxt?.$i18n?.locale) {
      // @ts-ignore
      languages = [window.$nuxt?.$i18n.locale, ...navigator.languages]
    }
    let q = 1.0
    const qDecrement = 0.1 // Decrement for each subsequent language

    return languages
      .map((lang, index) => {
        // For the first language, we don't usually specify the q-value as it's assumed to be 1.0
        if (index === 0) {
          return lang
        }

        // Construct the language with its q-value, then decrement the q-value for the next language
        const langWithQ = `${lang};q=${q.toFixed(1)}`
        q = Math.max(q - qDecrement, 0.1) // Ensures q doesn't go below 0.1
        return langWithQ
      })
      .join(',')
  }

  const customFetch = async (_, options: any) => {
    options.headers['Accept-Language'] = generateAcceptLanguageString()
    if (sessionStorage.getItem('ix-account-id')) {
      options.headers['Account-Id'] = sessionStorage.getItem('ix-account-id')
    }
    if (sessionStorage.getItem('impersonation_user_id')) {
      options.headers['Impersonation-User-Id'] = sessionStorage.getItem(
        'impersonation_user_id',
      )
    }
    // Must match backend CORS allow_origins + allow_origin_regex (app/main.py)
    const ALLOWED_DOMAINS = [
      'https://assessment.impactnexus.ai',
      'https://assessment.impactnexus.io',
      'https://assessment-berlin-mo57vsx7wa-oe.a.run.app',
      'https://ckic-only-test-1073300305602.europe-west4.run.app',
      'https://impact.climate-kic.org',
      'https://impact.borderstep.de',
      'https://assessment-20081367573.europe-west4.run.app', // Borderstep (Cloud Run URL, kept as fallback alongside the custom domain)
      'https://assessment-m6iffwfnwa-ez.a.run.app', // Borderstep (alternate Cloud Run URL alias for the same service)
      'http://localhost:3000',
    ]
    const ALLOWED_ORIGIN_REGEXES = [
      /^https:\/\/[^/]+\.impactnexus\.dev$/, // *.impactnexus.dev
      /^https:\/\/[^/]+\.impactnexus\.io$/, // *.impactnexus.io
      /^http:\/\/[^/]+\.ix-dev\.io:3000$/, // remote dev
    ]

    const origin = typeof window !== 'undefined' ? window.location.origin : ''

    const isAllowedOrigin =
      ALLOWED_DOMAINS.includes(origin) ||
      ALLOWED_ORIGIN_REGEXES.some((re) => re.test(origin))

    const graphqlBase =
      typeof window !== 'undefined'
        ? (isAllowedOrigin ? origin : null)
        : process.env.DOMAIN || ''

    if (!graphqlBase) {
      const error =
        typeof window !== 'undefined'
          ? `Origin '${origin}' is not allowed. Expected one of: ${ALLOWED_DOMAINS.join(', ')} or matching *.impactnexus.dev, *.impactnexus.io, *.ix-dev.io:3000`
          : 'GraphQL base URL could not be determined. Set DOMAIN env var for SSR.'
      throw new Error(error)
    }
    return fetch(`${graphqlBase}/graphql`, options).catch(
      // eslint-disable-next-line no-console
      (error) => {
        console.log(error)
        app?.$sentry?.captureException(error)
      },
    )
  }
  const httpOptions = {
    fetch: customFetch,
  }
  const httpLink = split(
    (operation) => operation.getContext().hasUpload,
    createUploadLink(httpOptions),
    createHttpLink(httpOptions),
  )
  const removeTypenameLink = removeTypenameFromVariables()

  const link = removeTypenameLink.concat(errorLink.concat(httpLink))

  // const mergeList = (existing, incoming) => {
  //   if (!existing) {
  //     return incoming
  //   }
  //   console.log('mergeList', existing)
  //   // debugger
  //   const merged = [...incoming]

  //   // Append unique items from array1 (those not present in array2)
  //   existing.forEach((item1) => {
  //     if (!merged.some((item2) => item2.__ref === item1.__ref)) {
  //       merged.push(item1)
  //     }
  //   })

  //   return merged
  // }
  const cache = new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          // listMetricAggregations: {
          //   keyArgs: ['limit', 'offset'],
          //   merge: mergeList,
          // },
          // listMetricAssignments: {
          //   keyArgs: false,
          //   merge: mergeList,
          // },
        },
      },
      MetricAggregationType: {
        keyFields: ['id'],
        // Don't cache separate results based on
        // any of this field's arguments.
        // keyArgs: false,

        // Concatenate the incoming list items with
        // the existing list items.
        merge: true,
      },
      UserInputConceptLinkType: {
        keyFields: ['conceptId', 'userInputId'],
        // Don't cache separate results based on
        // any of this field's arguments.
        // keyArgs: false,

        // Concatenate the incoming list items with
        // the existing list items.
        merge: false,
      },
      Post: {
        merge: true,
      },
    },
  }) // { addTypename: false }

  return {
    link,
    cache,
    defaultHttpLink: false,
    getAuth: async () => {
      if (window.location.href?.includes('register')) {
        // those pages don't need auth
        return (app.$auth?.strategy as any).token?.get()
      }
      if (app.$auth.strategy?.token?.status?.()?.expired()) {
        app.$auth.$storage.setUniversal('redirect', app.route?.fullPath) // set redirect path so we land back here after login
        if (!tokenRefreshPromise) {
          tokenRefreshPromise = app.$auth.refreshTokens().finally(() => {
            tokenRefreshPromise = null
          })
        }
        await tokenRefreshPromise
      }
      if ((app.$auth?.strategy as any).token.get() === undefined) {
        throw new Error('User is not authenticated')
      }

      return (app.$auth?.strategy as any).token?.get()
    },
  }
}
